import { Loading } from 'components/loading/Loading';
import React, { useEffect, useState } from 'react';
import { assignRoleToUser, getUserDetail } from 'services/api/users';
import { USER_DETAILS_TYPE, USER_ROLES, showError, showSuccess } from 'utils';
import styles from './index.module.scss';
import UserOverview from './UserOverview';
import BackButton from 'components/BackButton';
import TabView from 'components/TabView';
import UserOrders from './UserOrders';
import UserSubscriptions from './UserSubscriptions';
import UserSuppliers from './UserSuppliers';
import UsersInventory from './UserInventory';
import { fetchUserAddresses } from 'services';
import UserHomeBoxPreferences from './UserHomeBoxPreferences';
import ActionButton from 'components/ActionButton';

const tabs = [
  {
    key: USER_DETAILS_TYPE.OVERVIEW.key,
    label: USER_DETAILS_TYPE.OVERVIEW.label,
  },
  {
    key: USER_DETAILS_TYPE.ORDERS.key,
    label: USER_DETAILS_TYPE.ORDERS.label,
  },
  {
    key: USER_DETAILS_TYPE.SUBSCRIPTIONS.key,
    label: USER_DETAILS_TYPE.SUBSCRIPTIONS.label,
  },
  {
    key: USER_DETAILS_TYPE.USERS.key,
    label: USER_DETAILS_TYPE.USERS.label,
  },
  {
    key: USER_DETAILS_TYPE.SUPPLIERS.key,
    label: USER_DETAILS_TYPE.SUPPLIERS.label,
  },
  {
    key: USER_DETAILS_TYPE.HOME_BOX_PREFERENCES.key,
    label: USER_DETAILS_TYPE.HOME_BOX_PREFERENCES.label,
  },
];

const UserDetails = props => {
  const [loading, setLoading] = useState<boolean>(false);
  const [roleLoading, setRoleLoading] = useState<boolean>(false);
  const [user, setUser] = useState<Record<string, any>>({});
  const [account, setAccount] = useState<Record<string, any> | undefined>({});
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [userAddresses, setUserAddresses] = useState<Record<string, any>[]>([]);
  const [addressLoading, setAddressLoading] = useState<boolean>(false);
  const { userId } = props.match.params;

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const { user } = await getUserDetail(userId);
      setUser(user);
      if (user.account) {
        setAccount(user.account.toJSON());
      }
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAddressesFn = async () => {
    try {
      const response = await fetchUserAddresses(userId);
      setUserAddresses(response);
    } catch (e) {
      showError(e);
    } finally {
      setAddressLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchUserDetails();
    fetchUserAddressesFn();
  }, []);

  const assignRolesToUser = async () => {
    setRoleLoading(true);
    try {
      await assignRoleToUser({ userId, role: selectedRole });
      setSelectedRole('');
      fetchUserDetails();
      showSuccess('Role assigned successfully');
    } catch (e) {
      showError(e);
    } finally {
      setRoleLoading(false);
    }
  };

  const getPanels = () => {
    const panelsArr: any = [
      {
        key: USER_DETAILS_TYPE.OVERVIEW.key,
        Component: UserOverview,
        props: {
          user,
          account,
          userAddresses,
          addressLoading,
          refetchAddresses: fetchUserAddressesFn,
          updateUser: fetchUserDetails,
          setUser: setUser
        },
      },
      {
        key: USER_DETAILS_TYPE.ORDERS.key,
        Component: UserOrders,
        props: {
          ...props,
          userId,
        },
      },
      {
        key: USER_DETAILS_TYPE.SUBSCRIPTIONS.key,
        Component: UserSubscriptions,
        props: {
          ...props,
          userId,
        },
      },
      {
        key: USER_DETAILS_TYPE.USERS.key,
        Component: UsersInventory,
        props: {
          ...props,
          userId,
        },
      },
      {
        key: USER_DETAILS_TYPE.SUPPLIERS.key,
        Component: UserSuppliers,
        props: {
          ...props,
          userId,
        },
      },
      {
        key: USER_DETAILS_TYPE.HOME_BOX_PREFERENCES.key,
        Component: UserHomeBoxPreferences,
        props: {
          ...props,
          userId,
        },
      },
    ];

    return panelsArr;
  };

  const getAssignRoleSection = () => {
    const getOptions = () => Object.keys(USER_ROLES).map((role, i) => {
      return <option key={i} value={USER_ROLES[role]}>{USER_ROLES[role]}</option>;
    });

    return (
      <div className={styles['assign-role-section']}>
        <div className={styles['label']}>Assign Role</div>
        <div className={'d-flex align-items-end'}>
          <select
            className='form-control'
            value={selectedRole}
            // disabled={assignLoading}
            onChange={e => setSelectedRole(e.target.value)}
          >
            <option hidden={!!selectedRole} value={'default'}>Select a role</option>
            {getOptions()}
          </select>
          <ActionButton
            text='Assign'
            active={true}
            isLoading={false}
            customClass={'btn-primary'}
            onClick={assignRolesToUser}
          />
        </div>
      </div>
    );
  };

  const getHeader = () => (
    <div className={styles['header']}>
      <div className={styles['heading']}>User Details</div>
      <div className={styles['header-buttons']}>
        <BackButton />
      </div>
    </div>
  );

  if (loading) return <Loading />;

  return (
    <div className={styles['user-main-container']}>
      {roleLoading && <Loading />}
      {getHeader()}
      {getAssignRoleSection()}
      <TabView
        {...props}
        tabsList={tabs}
        tabsPanelList={getPanels()}
      />
    </div>
  );
};

export { UserDetails };